import { useEffect, useRef, useState } from 'react';
import { Sidebar } from './sidebar';
import { NotificationBell } from '@/components/notifications/notification-bell';
import { ErrorBoundary } from './error-boundary';
import { IdleTimeoutMonitor } from '@/components/auth/idle-timeout-monitor';
import { TimerIndicator } from '@/components/time/timer-indicator';
import { BroadcastBanner } from './broadcast-banner';
import { KnowledgeDrawer } from '@/components/knowledge/KnowledgeDrawer';
import { useTimerStore } from '@/stores/timer-store';
import { HelpCircle } from 'lucide-react';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const isRunning = useTimerStore((s) => s.isRunning);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Global timer tick — runs every second while the timer is active,
  // regardless of which page the user is on.
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        useTimerStore.getState().tick();
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning]);

  return (
    <div className="flex h-screen bg-[var(--bg-primary)]">
      <Sidebar />
      <IdleTimeoutMonitor />
      <main className="flex-1 overflow-auto relative">
        {/* Sticky header group — top bar + broadcast banner stick together on scroll */}
        <div className="sticky top-0 z-30 bg-[var(--bg-primary)]">
          {/* Desktop top bar — hidden on mobile (handled by Sidebar) */}
          <div className="hidden md:flex items-center px-6 lg:px-8 py-2.5 border-b border-[var(--border-color)] bg-[var(--bg-primary)]/80 backdrop-blur-md">
            <TimerIndicator />
            <button
              onClick={() => setKnowledgeOpen(true)}
              className="p-1.5 rounded-md hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors ml-auto mr-2"
              title="Help & Guides"
            >
              <HelpCircle size={18} />
            </button>
            <NotificationBell />
          </div>
          {/* Broadcast banner — below top bar, content-width */}
          <BroadcastBanner />
        </div>
        {/* Noise texture */}
        <div className="bg-noise opacity-[0.02] pointer-events-none fixed inset-0 z-0" />
        <div className="relative z-0 p-4 md:p-6 lg:p-8 pt-16 md:pt-2 lg:pt-4 animate-fade-in">
          <ErrorBoundary withinLayout>
            {children}
          </ErrorBoundary>
        </div>
      </main>
      <KnowledgeDrawer open={knowledgeOpen} onClose={() => setKnowledgeOpen(false)} />
    </div>
  );
}
